import * as React from 'react';
import {
    Alert, Box, Button, Card, CardActionArea, CardContent, Chip, CircularProgress, Link, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { Link as RouterLink } from 'react-router-dom';
import { getBetupFeedChannels, getMatchPublishing, triggerChannelArticle } from '../../Api';
import { BetupFeedChannelDto } from '../../models/dtos/BetupFeedChannelDto';
import { MatchPublishingDto, PublishingPostDto } from '../../models/dtos/PublishingDto';
import { channelName } from '../betupFeed/BetupFeedChannelDialogs';
import ChannelArticleExecutionDialog, { ArticleStageChip } from './ChannelArticleExecutionDialog';
import PublishingPostDialog from './PublishingPostDialog';

/** How long the tab keeps checking after a trigger for Crons to take it (it may wait behind others in the queue). */
const QUEUED_POLL_MS = 3 * 60_000;

/**
 * A match's Posts tab on the dashboard (docs/channel-articles.md §9): every channel post linked to the match, drafts
 * too, each opening with its card and editor; and "Draft article as [channel]", a manual AI article trigger for this
 * match on behalf of the chosen channel (no scheduler limits). An article invocation of the match in progress shows
 * with its stage.
 */
export default function MatchPostsTab({ matchId, sportId, onToast }: { matchId: number; sportId?: number | null; onToast: (message: string) => void }) {
    const [state, setState] = React.useState<MatchPublishingDto | null>(null);
    const [failed, setFailed] = React.useState(false);
    const [channels, setChannels] = React.useState<BetupFeedChannelDto[]>([]);
    const [channelId, setChannelId] = React.useState('');
    const [sending, setSending] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [pollUntil, setPollUntil] = React.useState(0);
    const [open, setOpen] = React.useState<PublishingPostDto | null>(null);
    const [executionOpen, setExecutionOpen] = React.useState(false);

    const load = React.useCallback(async () => {
        const result = await getMatchPublishing(matchId);
        setFailed(result == null);
        if (result) setState(result);
    }, [matchId]);

    React.useEffect(() => { load(); getBetupFeedChannels().then(result => setChannels(result ?? [])); }, [load]);

    const running = state?.running ?? null;
    React.useEffect(() => {
        if (!running && Date.now() > pollUntil) return;
        const timer = setInterval(() => {
            if (!running && Date.now() > pollUntil) clearInterval(timer);
            load();
        }, 5_000);
        return () => clearInterval(timer);
    }, [running, pollUntil, load]);

    // Channels covering the match's sport first, then the other active ones, inactive last.
    const ordered = React.useMemo(() => {
        const rank = (c: BetupFeedChannelDto) => (c.active ? 0 : 2) + (sportId != null && c.sport_ids?.includes(sportId) ? 0 : 1);
        return [...channels].sort((a, b) => rank(a) - rank(b) || channelName(a).localeCompare(channelName(b)));
    }, [channels, sportId]);
    const channelById = React.useMemo(() => new Map(channels.map(c => [c.id, c])), [channels]);

    const draft = async () => {
        if (!channelId) {
            setError('Choose the channel that writes about this match.');
            return;
        }
        setSending(true);
        setError(null);
        const result = await triggerChannelArticle(Number(channelId), matchId);
        setSending(false);
        if (result.error) {
            setError(result.error);
            return;
        }
        onToast(`Queued: an AI article about this match for ${channelName(channelById.get(Number(channelId))!)}`);
        setPollUntil(Date.now() + QUEUED_POLL_MS);
        setTimeout(load, 1500);
    };

    return (
        <Stack gap={2} sx={{ py: 2 }}>
            <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                <TextField select size="small" label="Channel" value={channelId} sx={{ minWidth: 240 }} onChange={e => setChannelId(e.target.value)}>
                    {ordered.map(c => (
                        <MenuItem key={c.id} value={String(c.id)}>
                            {channelName(c)}{c.active ? '' : ' (inactive)'}{sportId != null && c.sport_ids?.includes(sportId) ? '' : ' · other sports'}
                        </MenuItem>
                    ))}
                </TextField>
                <Button variant="contained" startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <EditNoteIcon />}
                    disabled={sending || running != null} onClick={draft}>
                    Draft article
                </Button>
                <Box sx={{ flex: 1 }} />
                <Link component={RouterLink} to={`/marketing/betup-feed/publishing?tab=invocations&match=${matchId}`} variant="body2">
                    AI article invocations of this match
                </Link>
            </Stack>
            <Typography variant="caption" color="text.secondary">
                The AI reuses the match's fresh web research (or researches it), writes in the channel's voice and languages and saves a
                draft here — or publishes it when auto-publish is on (BETUP Feed › Publishing).
            </Typography>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {failed && <Alert severity="error">The match's posts could not be loaded.</Alert>}
            {running && (
                <Alert severity="info" icon={<CircularProgress size={18} />}
                    action={<Button size="small" color="inherit" onClick={() => setExecutionOpen(true)}>Open</Button>}>
                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                        <span>AI article for {running.channel_name ?? `channel ${running.channel_id}`} in progress (invocation #{running.id}):</span>
                        <ArticleStageChip status={running.status} />
                    </Stack>
                </Alert>
            )}
            {!state && !failed && <CircularProgress size={24} />}
            {state && state.posts.length === 0 && <Typography variant="body2" color="text.secondary">No channel post about this match yet.</Typography>}
            {state?.posts.map(post => (
                <Card key={post.id} variant="outlined">
                    <CardActionArea onClick={() => setOpen(post)}>
                        <CardContent sx={{ py: 1.5 }}>
                            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                                <Typography variant="subtitle2">{post.channel_name ?? `#${post.channel_id}`}</Typography>
                                <Chip size="small" variant={post.draft ? 'outlined' : 'filled'} color={post.draft ? (post.will_publish_at ? 'info' : 'default') : 'success'}
                                    label={post.draft ? (post.will_publish_at ? 'Scheduled' : 'Draft') : 'Published'} />
                                {post.ai_execution_id && <Chip size="small" icon={<AutoAwesomeIcon />} label="AI" variant="outlined" color="secondary" />}
                                <Typography variant="caption" color="text.secondary">
                                    {new Date(post.occurred_at).toLocaleString()} · {Object.keys(post.texts ?? {}).join(', ')}
                                </Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary" noWrap>{plain(post.text)}</Typography>
                        </CardContent>
                    </CardActionArea>
                </Card>
            ))}
            <PublishingPostDialog post={open} channel={open ? channelById.get(open.channel_id) ?? null : null} onClose={() => setOpen(null)}
                onChanged={message => { onToast(message); load(); }} />
            <ChannelArticleExecutionDialog executionId={executionOpen && running ? running.id : null} onClose={() => { setExecutionOpen(false); load(); }} />
        </Stack>
    );
}

/** The post's text without its widget blocks, on one line. */
function plain(text: string): string {
    const lines: string[] = [];
    let inBlock = false;
    for (const raw of text.split(/\r?\n/)) {
        const line = raw.trim();
        if (line.startsWith('```')) {
            if (!(line.length > 3 && line.endsWith('```') && line.includes('{'))) inBlock = !inBlock;
            continue;
        }
        if (!inBlock && line) lines.push(line);
    }
    return lines.join(' ') || '—';
}
